import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Chip,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
} from '@mui/material';
import {
  TrendingUp,
  Timeline,
  Psychology,
  AttachMoney,
  ExpandMore,
  ExpandLess,
  ArrowBack,
  Diamond,
  Person,
  Warning,
  CheckCircle,
  Schedule,
  ChevronRight,
  Add,
  Delete,
  Edit,
  PersonAdd,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { useBrandColors } from '../hooks/useBrandColors';
import { Project } from '../types';
import { GanttChart, GanttPhase, GanttKeystone } from './GanttChart';
import { AIPlanningChat } from './AIPlanningChat';

// Types
interface TeamMemberInPhase {
  id: string;
  name: string;
  hours: number;
  costRate: number;
  billRate: number;
  totalCost: number;
  totalRevenue: number;
  startDate: string;
  endDate: string;
}

interface Phase {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'pending' | 'in_progress' | 'completed';
  teamMembers: TeamMemberInPhase[];
  budgetUsed: number;
  budgetTotal: number;
}

interface Keystone {
  id: string;
  name: string;
  date: string;
  type: 'review' | 'hand_in' | 'demo' | 'release' | 'go_live';
}

interface ProjectPlanningData {
  id: string;
  name: string;
  status: 'on_track' | 'at_risk' | 'behind';
  progress: number;
  deadline: string;
  budgetUsed: number;
  budgetTotal: number;
  phases: Phase[];
  keystones: Keystone[];
  isInternal?: boolean;
}

// Mock data for all projects
const mockProjects: ProjectPlanningData[] = [
  {
    id: '1',
    name: 'Website Redesign',
    status: 'on_track',
    progress: 75,
    deadline: '2026-02-15',
    budgetUsed: 4823,
    budgetTotal: 8000,
    phases: [
      {
        id: 'p1',
        name: 'Design Phase',
        startDate: '2026-01-02',
        endDate: '2026-01-15',
        status: 'completed',
        budgetUsed: 1575,
        budgetTotal: 2000,
        teamMembers: [
          { id: 'm1', name: 'Anna', hours: 24, costRate: 35, billRate: 75, totalCost: 840, totalRevenue: 1800, startDate: '2026-01-02', endDate: '2026-01-10' },
          { id: 'm2', name: 'Sofia', hours: 21, costRate: 35, billRate: 75, totalCost: 735, totalRevenue: 1575, startDate: '2026-01-08', endDate: '2026-01-15' },
        ],
      },
      {
        id: 'p2',
        name: 'Development',
        startDate: '2026-01-10',
        endDate: '2026-02-05',
        status: 'in_progress',
        budgetUsed: 3248,
        budgetTotal: 5000,
        teamMembers: [
          { id: 'm3', name: 'Erik', hours: 45, costRate: 40, billRate: 85, totalCost: 1800, totalRevenue: 3825, startDate: '2026-01-10', endDate: '2026-02-01' },
          { id: 'm4', name: 'Maria', hours: 28, costRate: 38, billRate: 70, totalCost: 1064, totalRevenue: 1960, startDate: '2026-01-15', endDate: '2026-02-05' },
          { id: 'm5', name: 'Johan', hours: 10, costRate: 38, billRate: 70, totalCost: 380, totalRevenue: 700, startDate: '2026-01-20', endDate: '2026-02-05' },
        ],
      },
      {
        id: 'p3',
        name: 'QA & Testing',
        startDate: '2026-02-01',
        endDate: '2026-02-15',
        status: 'pending',
        budgetUsed: 0,
        budgetTotal: 1000,
        teamMembers: [
          { id: 'm1', name: 'Anna', hours: 0, costRate: 35, billRate: 75, totalCost: 0, totalRevenue: 0, startDate: '2026-02-01', endDate: '2026-02-10' },
          { id: 'm3', name: 'Erik', hours: 0, costRate: 40, billRate: 85, totalCost: 0, totalRevenue: 0, startDate: '2026-02-05', endDate: '2026-02-15' },
        ],
      },
    ],
    keystones: [
      { id: 'k1', name: 'Design Review', date: '2026-01-15', type: 'review' },
      { id: 'k2', name: 'Beta Release', date: '2026-02-01', type: 'release' },
      { id: 'k3', name: 'Hand In', date: '2026-02-15', type: 'hand_in' },
    ],
  },
  {
    id: '2',
    name: 'Mobile App',
    status: 'at_risk',
    progress: 40,
    deadline: '2026-03-01',
    budgetUsed: 2100,
    budgetTotal: 5000,
    phases: [
      {
        id: 'p1',
        name: 'Planning',
        startDate: '2026-01-05',
        endDate: '2026-01-12',
        status: 'completed',
        budgetUsed: 600,
        budgetTotal: 800,
        teamMembers: [
          { id: 'm6', name: 'Lisa', hours: 15, costRate: 40, billRate: 80, totalCost: 600, totalRevenue: 1200, startDate: '2026-01-05', endDate: '2026-01-12' },
        ],
      },
      {
        id: 'p2',
        name: 'Development',
        startDate: '2026-01-12',
        endDate: '2026-02-20',
        status: 'in_progress',
        budgetUsed: 1500,
        budgetTotal: 3200,
        teamMembers: [
          { id: 'm7', name: 'Marcus', hours: 35, costRate: 42, billRate: 90, totalCost: 1470, totalRevenue: 3150, startDate: '2026-01-12', endDate: '2026-02-15' },
        ],
      },
    ],
    keystones: [
      { id: 'k1', name: 'MVP Demo', date: '2026-02-10', type: 'demo' },
      { id: 'k2', name: 'Go Live', date: '2026-03-01', type: 'go_live' },
    ],
  },
  {
    id: '3',
    name: 'Backend API',
    status: 'behind',
    progress: 25,
    deadline: '2026-01-30',
    budgetUsed: 1200,
    budgetTotal: 3000,
    phases: [
      {
        id: 'p1',
        name: 'Architecture',
        startDate: '2026-01-02',
        endDate: '2026-01-08',
        status: 'completed',
        budgetUsed: 400,
        budgetTotal: 500,
        teamMembers: [
          { id: 'm3', name: 'Erik', hours: 10, costRate: 40, billRate: 85, totalCost: 400, totalRevenue: 850, startDate: '2026-01-02', endDate: '2026-01-08' },
        ],
      },
      {
        id: 'p2',
        name: 'Implementation',
        startDate: '2026-01-08',
        endDate: '2026-01-25',
        status: 'in_progress',
        budgetUsed: 800,
        budgetTotal: 2000,
        teamMembers: [
          { id: 'm3', name: 'Erik', hours: 20, costRate: 40, billRate: 85, totalCost: 800, totalRevenue: 1700, startDate: '2026-01-08', endDate: '2026-01-25' },
        ],
      },
    ],
    keystones: [
      { id: 'k1', name: 'Code Review', date: '2026-01-20', type: 'review' },
      { id: 'k2', name: 'Hand In', date: '2026-01-30', type: 'hand_in' },
    ],
  },
];

// Constants
const HOURS_PER_DAY = 8;

// Helper: Calculate hours from date range
const calculateHoursFromDates = (startDate: string, endDate: string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  return days * HOURS_PER_DAY;
};

// Helper: Recalculate cost and revenue
const recalculateMemberFinancials = (member: TeamMemberInPhase): TeamMemberInPhase => {
  return {
    ...member,
    totalCost: member.hours * member.costRate,
    totalRevenue: member.hours * member.billRate,
  };
};

// Generate AI insights for a specific project
const generateProjectAIInsights = (project: ProjectPlanningData): string[] => {
  const insights: string[] = [];
  const totalHours = project.phases.reduce((sum, p) => sum + p.teamMembers.reduce((s, m) => s + (m.hours || 0), 0), 0);
  const totalBudget = project.phases.reduce((sum, p) => sum + (p.budgetTotal || 0), 0);
  const usedBudget = project.phases.reduce((sum, p) => sum + (p.budgetUsed || 0), 0);
  const budgetPercent = totalBudget > 0 ? Math.round((usedBudget / totalBudget) * 100) : 0;
  
  // Status-based insight
  if (project.status === 'on_track') {
    insights.push(`📈 Projektet går enligt plan. ${budgetPercent}% av budgeten använd.`);
  } else if (project.status === 'at_risk') {
    insights.push(`⚠️ Projektet riskerar försening. Överväg att omfördela resurser.`);
  } else if (project.status === 'behind') {
    insights.push(`🚨 Projektet är efter schemat. Behöver omedelbar åtgärd.`);
  }

  // Phase analysis
  const inProgressPhases = project.phases.filter(p => p.status === 'in_progress');
  if (inProgressPhases.length > 0) {
    const phase = inProgressPhases[0];
    const phaseHours = phase.teamMembers.reduce((s, m) => s + m.hours, 0);
    insights.push(`🔄 "${phase.name}" pågår med ${phaseHours}h loggat av ${phase.teamMembers.length} personer.`);
  }

  // Keystone proximity
  const now = new Date();
  const upcomingKeystones = project.keystones
    .filter(k => new Date(k.date) > now)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  if (upcomingKeystones.length > 0) {
    const next = upcomingKeystones[0];
    const daysUntil = Math.ceil((new Date(next.date).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    insights.push(`📍 Nästa keystone: "${next.name}" om ${daysUntil} dagar.`);
  }

  // Resource recommendation
  const memberHours: Record<string, number> = {};
  project.phases.forEach(p => {
    p.teamMembers.forEach(m => {
      memberHours[m.name] = (memberHours[m.name] || 0) + m.hours;
    });
  });
  const topMember = Object.entries(memberHours).sort((a, b) => b[1] - a[1])[0];
  if (topMember) {
    insights.push(`💡 ${topMember[0]} har högst arbetsbelastning (${topMember[1]}h). Balansera vid behov.`);
  }

  return insights;
};

export const ProjectPlanning: React.FC = () => {
  const brandColors = useBrandColors();
  const [projects, setProjects] = useState<ProjectPlanningData[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectPlanningData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPhases, setExpandedPhases] = useState<Record<string, boolean>>({});
  const [expandedBudgetPhases, setExpandedBudgetPhases] = useState<Record<string, boolean>>({});
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiChatProjectId, setAiChatProjectId] = useState<string | null>(null);
  const [aiChatProjectName, setAiChatProjectName] = useState<string>('');
  const [showDetailAIChat, setShowDetailAIChat] = useState(false);
  const [deletingPhase, setDeletingPhase] = useState<string | null>(null);
  
  // Edit phase dialog state
  const [editingPhase, setEditingPhase] = useState<Phase | null>(null);
  const [editPhaseForm, setEditPhaseForm] = useState<{
    name: string;
    startDate: string;
    endDate: string;
    status: 'pending' | 'in_progress' | 'completed';
    teamMembers: Array<TeamMemberInPhase & { 
      salaryType: 'hourly' | 'monthly';
      monthlySalary: number;
      monthlyBillRate: number;
    }>;
  } | null>(null);
  const [savingPhase, setSavingPhase] = useState(false);
  const [workspaceMembers, setWorkspaceMembers] = useState<Array<{ id: string; name: string; costRate?: number; billRate?: number }>>([]);

  // Keystone edit dialog state
  const [editingKeystone, setEditingKeystone] = useState<Keystone | null>(null);
  const [keystoneForm, setKeystoneForm] = useState<{
    name: string;
    date: string;
    type: 'review' | 'hand_in' | 'demo' | 'release' | 'go_live';
  } | null>(null);
  const [savingKeystone, setSavingKeystone] = useState(false);

  // Monthly hours constant (21 work days × 8 hours)
  const MONTHLY_HOURS = 168;

  // Keystone handlers
  const handleKeystoneEdit = (keystone: Keystone) => {
    setEditingKeystone(keystone);
    setKeystoneForm({
      name: keystone.name,
      date: keystone.date,
      type: keystone.type,
    });
  };

  const handleCloseKeystoneDialog = () => {
    setEditingKeystone(null);
    setKeystoneForm(null);
  };

  const handleSaveKeystone = async () => {
    if (!editingKeystone || !keystoneForm || !selectedProject) return;
    
    setSavingKeystone(true);
    try {
      const updatedKeystone: Keystone = {
        ...editingKeystone,
        name: keystoneForm.name,
        date: keystoneForm.date,
        type: keystoneForm.type,
      };

      // Try to save to API (if endpoint exists)
      try {
        await apiService.updateKeystone(editingKeystone.id, updatedKeystone);
      } catch (apiErr) {
        console.log('API update not available, updating locally');
      }

      // Update local state
      setSelectedProject({
        ...selectedProject,
        keystones: selectedProject.keystones.map(k =>
          k.id === editingKeystone.id ? updatedKeystone : k
        ),
      });

      handleCloseKeystoneDialog();
    } catch (err) {
      console.error('Failed to save keystone:', err);
      alert('Kunde inte spara keystonen. Försök igen.');
    } finally {
      setSavingKeystone(false);
    }
  };

  const handleDeleteKeystone = async (keystoneId: string) => {
    const keystone = selectedProject?.keystones.find(k => k.id === keystoneId);
    if (!keystone || !selectedProject) return;

    if (!window.confirm(`Är du säker på att du vill ta bort keystonen "${keystone.name}"?`)) {
      return;
    }

    try {
      // Try to delete via API
      try {
        await apiService.deleteKeystone(keystoneId);
      } catch (apiErr) {
        console.log('API delete not available, deleting locally');
      }

      // Update local state
      setSelectedProject({
        ...selectedProject,
        keystones: selectedProject.keystones.filter(k => k.id !== keystoneId),
      });
    } catch (err) {
      console.error('Failed to delete keystone:', err);
      alert('Kunde inte ta bort keystonen. Försök igen.');
    }
  };

  // Load workspace members for dropdown
  const loadWorkspaceMembers = async () => {
    try {
      const members = await apiService.getWorkspaceMembers();
      setWorkspaceMembers(members || []);
    } catch (err) {
      console.error('Failed to load workspace members:', err);
      setWorkspaceMembers([]);
    }
  };

  // Open edit dialog
  const handleEditPhase = (phase: Phase) => {
    // Load workspace members for dropdown
    loadWorkspaceMembers();
    
    setEditingPhase(phase);
    setEditPhaseForm({
      name: phase.name,
      startDate: phase.startDate,
      endDate: phase.endDate,
      status: phase.status,
      teamMembers: phase.teamMembers.map(m => ({
        ...m,
        salaryType: 'hourly' as const,
        monthlySalary: m.costRate * MONTHLY_HOURS,
        monthlyBillRate: m.billRate * MONTHLY_HOURS,
      })),
    });
  };

  // Close edit dialog
  const handleCloseEditDialog = () => {
    setEditingPhase(null);
    setEditPhaseForm(null);
  };

  // Update member in form
  const updateMemberInForm = (memberId: string, updates: Partial<typeof editPhaseForm extends null ? never : NonNullable<typeof editPhaseForm>['teamMembers'][0]>) => {
    if (!editPhaseForm) return;
    setEditPhaseForm({
      ...editPhaseForm,
      teamMembers: editPhaseForm.teamMembers.map(m => {
        if (m.id !== memberId) return m;
        const updated = { ...m, ...updates };
        
        // Recalculate costRate/billRate if salary type or monthly values changed
        if (updated.salaryType === 'monthly') {
          updated.costRate = Math.round((updated.monthlySalary / MONTHLY_HOURS) * 100) / 100;
          updated.billRate = Math.round((updated.monthlyBillRate / MONTHLY_HOURS) * 100) / 100;
        }
        
        // Recalculate totals
        updated.totalCost = updated.hours * updated.costRate;
        updated.totalRevenue = updated.hours * updated.billRate;
        
        return updated;
      }),
    });
  };

  // Add new member to form
  const addMemberToForm = () => {
    if (!editPhaseForm) return;
    const newMember = {
      id: `new-${Date.now()}`,
      name: '',
      hours: 0,
      costRate: 0,
      billRate: 0,
      totalCost: 0,
      totalRevenue: 0,
      startDate: editPhaseForm.startDate,
      endDate: editPhaseForm.endDate,
      salaryType: 'hourly' as const,
      monthlySalary: 0,
      monthlyBillRate: 0,
    };
    setEditPhaseForm({
      ...editPhaseForm,
      teamMembers: [...editPhaseForm.teamMembers, newMember],
    });
  };

  // Remove member from form
  const removeMemberFromForm = (memberId: string) => {
    if (!editPhaseForm) return;
    setEditPhaseForm({
      ...editPhaseForm,
      teamMembers: editPhaseForm.teamMembers.filter(m => m.id !== memberId),
    });
  };

  // Save edited phase
  const handleSavePhase = async () => {
    if (!editingPhase || !editPhaseForm || !selectedProject) return;
    
    setSavingPhase(true);
    try {
      // Prepare data for API
      const updatedPhase: Phase = {
        ...editingPhase,
        name: editPhaseForm.name,
        startDate: editPhaseForm.startDate,
        endDate: editPhaseForm.endDate,
        status: editPhaseForm.status,
        teamMembers: editPhaseForm.teamMembers.map(m => ({
          id: m.id,
          name: m.name,
          hours: m.hours,
          costRate: m.costRate,
          billRate: m.billRate,
          totalCost: m.totalCost,
          totalRevenue: m.totalRevenue,
          startDate: m.startDate,
          endDate: m.endDate,
        })),
        budgetUsed: editPhaseForm.teamMembers.reduce((sum, m) => sum + m.totalCost, 0),
        budgetTotal: editingPhase.budgetTotal,
      };

      // Try to save to API (if endpoint exists)
      try {
        await apiService.updatePhase(editingPhase.id, updatedPhase);
      } catch (apiErr) {
        console.log('API update not available, updating locally');
      }

      // Update local state
      setSelectedProject({
        ...selectedProject,
        phases: selectedProject.phases.map(p =>
          p.id === editingPhase.id ? updatedPhase : p
        ),
      });

      handleCloseEditDialog();
    } catch (err) {
      console.error('Failed to save phase:', err);
      alert('Kunde inte spara ändringarna. Försök igen.');
    } finally {
      setSavingPhase(false);
    }
  };

  // Delete phase handler
  const handleDeletePhase = async (phaseId: string, phaseName: string) => {
    if (!window.confirm(`Är du säker på att du vill ta bort fasen "${phaseName}"? Detta kan inte ångras.`)) {
      return;
    }

    setDeletingPhase(phaseId);
    try {
      await apiService.deletePhase(phaseId);
      // Update local state
      if (selectedProject) {
        setSelectedProject({
          ...selectedProject,
          phases: selectedProject.phases.filter(p => p.id !== phaseId),
        });
      }
    } catch (err) {
      console.error('Failed to delete phase:', err);
      alert('Kunde inte ta bort fasen. Försök igen.');
    } finally {
      setDeletingPhase(null);
    }
  };

  // Load projects from API
  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAllProjectsPlanning();
      // Ensure all projects have required arrays
      const validData = (data || []).map((p: any) => ({
        ...p,
        phases: p.phases || [],
        keystones: p.keystones || [],
        budgetUsed: p.budgetUsed || 0,
        budgetTotal: p.budgetTotal || 0,
        progress: p.progress || 0,
        status: p.status || 'on_track',
        deadline: p.deadline || new Date().toISOString().split('T')[0],
      }));
      setProjects(validData);
    } catch (err) {
      console.error('Failed to load projects planning:', err);
      setError('Failed to load projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  // Load project detail from API
  const loadProjectDetail = async (projectId: string) => {
    try {
      const data = await apiService.getProjectPlanning(projectId);
      if (data) {
        setSelectedProject(data);
      }
    } catch (err) {
      console.error('Failed to load project detail:', err);
      // Keep using the clicked project from list
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const togglePhase = (phaseId: string) => {
    setExpandedPhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  const toggleBudgetPhase = (phaseId: string) => {
    setExpandedBudgetPhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on_track': return '#27ae60';
      case 'at_risk': return '#f39c12';
      case 'behind': return '#e74c3c';
      case 'completed': return '#27ae60';
      case 'in_progress': return '#3498db';
      case 'pending': return '#95a5a6';
      default: return '#95a5a6';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'on_track': return 'On Track';
      case 'at_risk': return 'At Risk';
      case 'behind': return 'Behind';
      default: return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'on_track': return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'at_risk': return <Warning sx={{ fontSize: 16 }} />;
      case 'behind': return <Schedule sx={{ fontSize: 16 }} />;
      default: return null;
    }
  };

  const formatCurrency = (amount: number) => `$${amount.toLocaleString()}`;
  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('default', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Project Detail View
  if (selectedProject) {
    return (
      <Box sx={{ p: 3, backgroundColor: '#fff', minHeight: '100vh' }}>
        {/* Header with Back Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => setSelectedProject(null)}>
            <ArrowBack />
          </IconButton>
          <TrendingUp sx={{ fontSize: 28, color: brandColors.primary }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {selectedProject.name}
          </Typography>
          <Chip
            icon={getStatusIcon(selectedProject.status) || undefined}
            label={getStatusLabel(selectedProject.status)}
            size="small"
            sx={{
              backgroundColor: getStatusColor(selectedProject.status) + '20',
              color: getStatusColor(selectedProject.status),
              fontWeight: 600,
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Chat with AI Planner">
            <Button
              variant="outlined"
              startIcon={<Psychology />}
              onClick={() => setShowDetailAIChat(!showDetailAIChat)}
              sx={{ 
                borderColor: '#9b59b6', 
                color: '#9b59b6',
                backgroundColor: showDetailAIChat ? '#9b59b620' : 'transparent',
                '&:hover': { 
                  borderColor: '#8e44ad', 
                  backgroundColor: '#9b59b610',
                } 
              }}
            >
              AI Planner
            </Button>
          </Tooltip>
        </Box>

        {/* Gantt Chart with Expandable Phases and Keystones */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Timeline /> Timeline
              <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                Drag bars to move • Resize from edges • Keystones are vertical lines
              </Typography>
            </Typography>

            <GanttChart
              phases={selectedProject.phases.map(phase => ({
                id: phase.id,
                name: phase.name,
                startDate: phase.startDate,
                endDate: phase.endDate,
                status: phase.status,
                teamMembers: phase.teamMembers.map(m => ({
                  id: m.id,
                  name: m.name,
                  hours: m.hours,
                  startDate: m.startDate,
                  endDate: m.endDate,
                })),
              })) as GanttPhase[]}
              keystones={selectedProject.keystones as GanttKeystone[]}
              onPhaseUpdate={(updatedPhase) => {
                setSelectedProject(prev => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    phases: prev.phases.map(p =>
                      p.id === updatedPhase.id
                        ? { ...p, startDate: updatedPhase.startDate, endDate: updatedPhase.endDate }
                        : p
                    ),
                  };
                });
                console.log('Phase updated:', updatedPhase);
              }}
              onMemberUpdate={(phaseId, updatedMember) => {
                setSelectedProject(prev => {
                  if (!prev) return prev;
                  // Calculate new hours based on date range
                  const newHours = calculateHoursFromDates(updatedMember.startDate, updatedMember.endDate);
                  return {
                    ...prev,
                    phases: prev.phases.map(p =>
                      p.id === phaseId
                        ? {
                            ...p,
                            teamMembers: p.teamMembers.map(m => {
                              if (m.id === updatedMember.id) {
                                const updated = {
                                  ...m,
                                  startDate: updatedMember.startDate,
                                  endDate: updatedMember.endDate,
                                  hours: newHours,
                                };
                                return recalculateMemberFinancials(updated);
                              }
                              return m;
                            }),
                          }
                        : p
                    ),
                  };
                });
                console.log('Member updated with hours recalc:', phaseId, updatedMember);
              }}
              onKeystoneUpdate={(updatedKeystone) => {
                setSelectedProject(prev => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    keystones: prev.keystones.map(k =>
                      k.id === updatedKeystone.id
                        ? { ...k, date: updatedKeystone.date }
                        : k
                    ),
                  };
                });
                console.log('Keystone updated:', updatedKeystone);
              }}
              onKeystoneEdit={(keystone) => handleKeystoneEdit(keystone as Keystone)}
              onKeystoneDelete={handleDeleteKeystone}
            />
          </CardContent>
        </Card>

        {/* AI Insights for this project */}
        <Card sx={{ mb: 3, backgroundColor: '#f8f9ff', border: '1px solid #e0e0ff' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Psychology sx={{ color: '#9b59b6' }} /> AI Insights
            </Typography>
            {generateProjectAIInsights(selectedProject).map((insight, idx) => (
              <Box
                key={idx}
                sx={{
                  backgroundColor: '#fff',
                  border: '1px solid #e9ecef',
                  borderRadius: 1,
                  p: 1.5,
                  mb: 1,
                }}
              >
                <Typography variant="body2">{insight}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* Phase & Time */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Schedule /> Phase & Time
            </Typography>

            {selectedProject.phases.map((phase) => (
              <Box key={phase.id} sx={{ mb: 2 }}>
                {/* Phase Budget Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f0f0f0' },
                  }}
                  onClick={() => toggleBudgetPhase(phase.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {expandedBudgetPhases[phase.id] ? <ExpandLess /> : <ExpandMore />}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {phase.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2">
                      {formatCurrency(phase.budgetUsed)} / {formatCurrency(phase.budgetTotal)}
                    </Typography>
                    <Box sx={{ width: 100 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(phase.budgetUsed / phase.budgetTotal) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 1,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getStatusColor(phase.status),
                          },
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: getStatusColor(phase.status) }}>
                      {Math.round((phase.budgetUsed / phase.budgetTotal) * 100)}%
                    </Typography>
                    <Tooltip title="Redigera fas">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditPhase(phase);
                        }}
                        sx={{ 
                          color: '#3498db',
                          opacity: 0.6,
                          '&:hover': { opacity: 1, backgroundColor: '#e8f4fc' },
                        }}
                      >
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ta bort fas">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePhase(phase.id, phase.name);
                        }}
                        disabled={deletingPhase === phase.id}
                        sx={{ 
                          color: '#e74c3c',
                          opacity: 0.6,
                          '&:hover': { opacity: 1, backgroundColor: '#fee' },
                        }}
                      >
                        {deletingPhase === phase.id ? <CircularProgress size={16} /> : <Delete sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Team Members Budget (Expandable) */}
                <Collapse in={expandedBudgetPhases[phase.id]}>
                  <Box sx={{ pl: 4, pt: 1 }}>
                    {phase.teamMembers.map((member) => (
                      <Box
                        key={member.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 1,
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2">{member.name}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Typography variant="caption" color="textSecondary">
                            {member.hours}h × ${member.costRate}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#e74c3c', minWidth: 80, textAlign: 'right' }}>
                            {formatCurrency(member.totalCost)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            bill: ${member.billRate}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#27ae60', fontWeight: 600, minWidth: 80, textAlign: 'right' }}>
                            {formatCurrency(member.totalRevenue)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* AI Planning Chat for Detail View */}
        {showDetailAIChat && (
          <AIPlanningChat
            projectId={selectedProject.id}
            projectName={selectedProject.name}
            onClose={() => setShowDetailAIChat(false)}
            onPlanAccepted={async () => {
              setShowDetailAIChat(false);
              // Reload project details
              try {
                const data = await apiService.getProjectPlanning(selectedProject.id);
                if (data) {
                  setSelectedProject(data);
                }
              } catch (err) {
                console.error('Failed to reload project:', err);
              }
            }}
          />
        )}

        {/* Edit Phase Dialog */}
        <Dialog
          open={!!editingPhase && !!editPhaseForm}
          onClose={handleCloseEditDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit sx={{ color: '#3498db' }} />
            Redigera fas: {editPhaseForm?.name}
          </DialogTitle>
          <DialogContent>
            {editPhaseForm && (
              <Box sx={{ pt: 1 }}>
                {/* Phase Info */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2, mb: 3 }}>
                  <TextField
                    label="Fasnamn"
                    value={editPhaseForm.name}
                    onChange={(e) => setEditPhaseForm({ ...editPhaseForm, name: e.target.value })}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Startdatum"
                    type="date"
                    value={editPhaseForm.startDate}
                    onChange={(e) => setEditPhaseForm({ ...editPhaseForm, startDate: e.target.value })}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Slutdatum"
                    type="date"
                    value={editPhaseForm.endDate}
                    onChange={(e) => setEditPhaseForm({ ...editPhaseForm, endDate: e.target.value })}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={editPhaseForm.status}
                      label="Status"
                      onChange={(e) => setEditPhaseForm({ ...editPhaseForm, status: e.target.value as any })}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Team Members */}
                <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person /> Teammedlemmar
                </Typography>
                
                {editPhaseForm.teamMembers.map((member, idx) => (
                  <Box
                    key={member.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: '#f8f9fa',
                      borderRadius: 1,
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 2, mb: 2 }}>
                      <Autocomplete
                        freeSolo
                        options={workspaceMembers}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                        value={workspaceMembers.find(m => m.name === member.name) || member.name}
                        onChange={(_, newValue) => {
                          if (typeof newValue === 'string') {
                            updateMemberInForm(member.id, { name: newValue });
                          } else if (newValue) {
                            // Auto-fill rates from workspace member
                            updateMemberInForm(member.id, { 
                              name: newValue.name,
                              costRate: newValue.costRate || member.costRate,
                              billRate: newValue.billRate || member.billRate,
                            });
                          }
                        }}
                        onInputChange={(_, newInputValue) => {
                          if (newInputValue !== member.name) {
                            updateMemberInForm(member.id, { name: newInputValue });
                          }
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Namn" size="small" />
                        )}
                        size="small"
                      />
                      <TextField
                        label="Timmar"
                        type="number"
                        value={member.hours}
                        onChange={(e) => updateMemberInForm(member.id, { hours: parseFloat(e.target.value) || 0 })}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Startdatum"
                        type="date"
                        value={member.startDate}
                        onChange={(e) => updateMemberInForm(member.id, { startDate: e.target.value })}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="Slutdatum"
                        type="date"
                        value={member.endDate}
                        onChange={(e) => updateMemberInForm(member.id, { endDate: e.target.value })}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeMemberFromForm(member.id)}
                        sx={{ color: '#e74c3c', mt: 0.5 }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>

                    {/* Salary Type Toggle */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Chip
                        label="Timpris"
                        onClick={() => updateMemberInForm(member.id, { salaryType: 'hourly' })}
                        sx={{
                          backgroundColor: member.salaryType === 'hourly' ? '#3498db' : '#e0e0e0',
                          color: member.salaryType === 'hourly' ? '#fff' : '#666',
                          cursor: 'pointer',
                        }}
                      />
                      <Chip
                        label="Månadslön"
                        onClick={() => updateMemberInForm(member.id, { salaryType: 'monthly' })}
                        sx={{
                          backgroundColor: member.salaryType === 'monthly' ? '#9b59b6' : '#e0e0e0',
                          color: member.salaryType === 'monthly' ? '#fff' : '#666',
                          cursor: 'pointer',
                        }}
                      />
                    </Box>

                    {/* Cost & Bill Rate */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2 }}>
                      {member.salaryType === 'hourly' ? (
                        <>
                          <TextField
                            label="Kostnad/h ($)"
                            type="number"
                            value={member.costRate}
                            onChange={(e) => updateMemberInForm(member.id, { costRate: parseFloat(e.target.value) || 0 })}
                            fullWidth
                            size="small"
                          />
                          <TextField
                            label="Debitering/h ($)"
                            type="number"
                            value={member.billRate}
                            onChange={(e) => updateMemberInForm(member.id, { billRate: parseFloat(e.target.value) || 0 })}
                            fullWidth
                            size="small"
                            helperText="Sätt $0 för internt"
                          />
                        </>
                      ) : (
                        <>
                          <TextField
                            label="Månadslön ($)"
                            type="number"
                            value={member.monthlySalary}
                            onChange={(e) => updateMemberInForm(member.id, { monthlySalary: parseFloat(e.target.value) || 0 })}
                            fullWidth
                            size="small"
                            helperText={`→ $${member.costRate}/h`}
                          />
                          <TextField
                            label="Månadsdebitering ($)"
                            type="number"
                            value={member.monthlyBillRate}
                            onChange={(e) => updateMemberInForm(member.id, { monthlyBillRate: parseFloat(e.target.value) || 0 })}
                            fullWidth
                            size="small"
                            helperText={`→ $${member.billRate}/h (0 för internt)`}
                          />
                        </>
                      )}
                      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography variant="caption" color="textSecondary">Kostnad</Typography>
                        <Typography variant="body2" sx={{ color: '#e74c3c', fontWeight: 600 }}>
                          ${member.totalCost.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography variant="caption" color="textSecondary">Intäkt</Typography>
                        <Typography variant="body2" sx={{ color: member.totalRevenue > 0 ? '#27ae60' : '#95a5a6', fontWeight: 600 }}>
                          ${member.totalRevenue.toLocaleString()}
                          {member.totalRevenue === 0 && <span style={{ fontSize: 10, marginLeft: 4 }}>(internt)</span>}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}

                {/* Add Member Button */}
                <Button
                  startIcon={<PersonAdd />}
                  onClick={addMemberToForm}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Lägg till person
                </Button>

                {/* Summary */}
                <Box sx={{ mt: 3, p: 2, backgroundColor: '#e8f4fc', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Sammanfattning</Typography>
                  <Box sx={{ display: 'flex', gap: 4 }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">Total kostnad</Typography>
                      <Typography variant="h6" sx={{ color: '#e74c3c' }}>
                        ${editPhaseForm.teamMembers.reduce((sum, m) => sum + m.totalCost, 0).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary">Total intäkt</Typography>
                      <Typography variant="h6" sx={{ color: '#27ae60' }}>
                        ${editPhaseForm.teamMembers.reduce((sum, m) => sum + m.totalRevenue, 0).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary">Marginal</Typography>
                      <Typography variant="h6" sx={{ color: '#3498db' }}>
                        ${(editPhaseForm.teamMembers.reduce((sum, m) => sum + m.totalRevenue, 0) - editPhaseForm.teamMembers.reduce((sum, m) => sum + m.totalCost, 0)).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Avbryt</Button>
            <Button
              onClick={handleSavePhase}
              variant="contained"
              disabled={savingPhase}
              startIcon={savingPhase ? <CircularProgress size={16} /> : null}
            >
              Spara ändringar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Keystone Dialog */}
        <Dialog
          open={!!editingKeystone && !!keystoneForm}
          onClose={handleCloseKeystoneDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Diamond sx={{ color: '#9b59b6' }} />
            Redigera Keystone: {keystoneForm?.name}
          </DialogTitle>
          <DialogContent>
            {keystoneForm && (
              <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Namn"
                  value={keystoneForm.name}
                  onChange={(e) => setKeystoneForm({ ...keystoneForm, name: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Datum"
                  type="date"
                  value={keystoneForm.date}
                  onChange={(e) => setKeystoneForm({ ...keystoneForm, date: e.target.value })}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Typ</InputLabel>
                  <Select
                    value={keystoneForm.type}
                    label="Typ"
                    onChange={(e) => setKeystoneForm({ ...keystoneForm, type: e.target.value as typeof keystoneForm.type })}
                  >
                    <MenuItem value="review">🔍 Review</MenuItem>
                    <MenuItem value="hand_in">📦 Hand In</MenuItem>
                    <MenuItem value="demo">🎬 Demo</MenuItem>
                    <MenuItem value="release">🚀 Release</MenuItem>
                    <MenuItem value="go_live">🌐 Go Live</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseKeystoneDialog}>Avbryt</Button>
            <Button
              onClick={handleSaveKeystone}
              variant="contained"
              disabled={savingKeystone}
              startIcon={savingKeystone ? <CircularProgress size={16} /> : null}
              sx={{ backgroundColor: '#9b59b6', '&:hover': { backgroundColor: '#8e44ad' } }}
            >
              Spara
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Main Projects List View
  return (
    <Box sx={{ p: 3, backgroundColor: '#fff', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <TrendingUp sx={{ fontSize: 32, color: brandColors.primary }} />
        <Typography variant="h4" sx={{ fontWeight: 600, color: brandColors.textPrimary }}>
          Project Planning
        </Typography>
        <Chip
          label="🔒 Super Owner Only"
          size="small"
          sx={{ backgroundColor: '#fff3cd', color: '#856404' }}
        />
      </Box>

      {/* Projects List */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            All Projects
          </Typography>

          {/* Table Header */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1.5fr 1fr 1.5fr 0.5fr',
              gap: 2,
              p: 1.5,
              backgroundColor: '#f8f9fa',
              borderRadius: 1,
              mb: 1,
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            <Box>Project Name</Box>
            <Box>Status</Box>
            <Box>Progress</Box>
            <Box>Deadline</Box>
            <Box>Time</Box>
            <Box></Box>
          </Box>

          {/* Project Rows */}
          {projects.map((project) => (
            <Box
              key={project.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1.5fr 1fr 1.5fr 0.5fr',
                gap: 2,
                p: 1.5,
                borderBottom: '1px solid #f0f0f0',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#fafafa' },
              }}
              onClick={async () => {
                // Set basic data immediately
                setSelectedProject(project);
                // Then load full details from API
                try {
                  const data = await apiService.getProjectPlanning(project.id);
                  if (data) {
                    setSelectedProject(data);
                  }
                } catch (err) {
                  console.error('Failed to load full project details:', err);
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {project.name}
                </Typography>
              </Box>
              <Box>
                <Chip
                  icon={getStatusIcon(project.status) || undefined}
                  label={getStatusLabel(project.status)}
                  size="small"
                  sx={{
                    backgroundColor: getStatusColor(project.status) + '20',
                    color: getStatusColor(project.status),
                    fontWeight: 500,
                    fontSize: 11,
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={project.progress}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getStatusColor(project.status),
                      },
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 35 }}>
                  {project.progress}%
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2">{formatDate(project.deadline)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2">
                  {(() => {
                    // Calculate total hours from phases
                    const totalHoursUsed = project.phases.reduce((sum, p) => 
                      sum + p.teamMembers.reduce((s, m) => s + (m.hours || 0), 0), 0
                    );
                    const totalHoursBudget = project.budgetTotal > 0 
                      ? Math.round(project.budgetTotal / 50) // Estimate: $50/hour average
                      : project.phases.reduce((sum, p) => 
                          sum + p.teamMembers.reduce((s, m) => s + calculateHoursFromDates(m.startDate, m.endDate), 0), 0
                        );
                    return `${totalHoursUsed}h / ${totalHoursBudget}h`;
                  })()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Plan with AI">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAiChatProjectId(project.id);
                      setAiChatProjectName(project.name);
                      setShowAIChat(true);
                    }}
                    sx={{ color: '#9b59b6' }}
                  >
                    <Psychology />
                  </IconButton>
                </Tooltip>
                <Tooltip title="View details">
                  <IconButton size="small">
                    <ChevronRight />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* AI Planning Chat */}
      {showAIChat && aiChatProjectId && (
        <AIPlanningChat
          projectId={aiChatProjectId}
          projectName={aiChatProjectName}
          onClose={() => {
            setShowAIChat(false);
            setAiChatProjectId(null);
          }}
          onPlanAccepted={() => {
            setShowAIChat(false);
            setAiChatProjectId(null);
            loadProjects(); // Reload to show new phases
          }}
        />
      )}
    </Box>
  );
};

export default ProjectPlanning;
